import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LinesSkeleton } from "@/components/Skeletons";
import { Plus, Trash2, Edit2, BookOpen } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Leaks", "Drains", "Water Heaters", "Toilets", "Faucets", "Pipes", "Sewer", "Gas", "Inspection", "Emergency"];

export default function PriceBook() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: "Leaks", name: "", labor_hours: 1, material_cost: 0, price: 100 });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setItems(null);
    const { data } = await api.get("/price-book");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    if (!items) return {};
    const q = search.trim().toLowerCase();
    const filtered = q ? items.filter((i) => i.name.toLowerCase().includes(q) || i.category.toLowerCase().includes(q)) : items;
    return filtered.reduce((acc, it) => {
      (acc[it.category] = acc[it.category] || []).push(it);
      return acc;
    }, {});
  }, [items, search]);

  const startCreate = () => {
    setEditing(null);
    setForm({ category: "Leaks", name: "", labor_hours: 1, material_cost: 0, price: 100 });
    setOpen(true);
  };
  const startEdit = (it) => {
    setEditing(it);
    setForm({ category: it.category, name: it.name, labor_hours: it.labor_hours, material_cost: it.material_cost, price: it.price });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, labor_hours: Number(form.labor_hours) || 0, material_cost: Number(form.material_cost) || 0, price: Number(form.price) || 0 };
      if (editing) {
        await api.put(`/price-book/${editing.id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/price-book", payload);
        toast.success("Service added");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try {
      await api.delete(`/price-book/${it.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">Price Book</h1>
        <Button onClick={startCreate} className="h-12 px-4 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="add-service-btn">
          <Plus className="w-5 h-5 mr-1.5" />
          <span className="hidden sm:inline">Add Service</span>
          <span className="sm:hidden">Add</span>
        </Button>
      </div>

      <Input
        placeholder="Search services…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-12 bg-white"
        data-testid="price-search"
      />

      {items === null ? (
        <LinesSkeleton count={8} />
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No services yet.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2" data-testid={`category-${cat}`}>{cat}</h2>
              <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
                {list.map((it) => (
                  <div key={it.id} className="p-4 flex items-center gap-3" data-testid={`price-item-${it.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{it.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {it.labor_hours}h labor · ${Number(it.material_cost).toFixed(2)} materials
                      </div>
                    </div>
                    <div className="font-bold text-lg text-slate-900" style={{ fontFamily: "Outfit" }}>${Number(it.price).toFixed(2)}</div>
                    <button onClick={() => startEdit(it)} className="h-9 w-9 rounded-lg text-slate-600 hover:bg-slate-100 flex items-center justify-center" data-testid={`edit-${it.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(it)} className="h-9 w-9 rounded-lg text-red-600 hover:bg-red-50 flex items-center justify-center" data-testid={`delete-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-12 mt-1.5" data-testid="service-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Service Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 mt-1.5" data-testid="service-name" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-sm font-semibold">Labor (h)</Label>
                <Input type="number" step="0.25" value={form.labor_hours} onChange={(e) => setForm({ ...form, labor_hours: e.target.value })} className="h-12 mt-1.5" data-testid="service-labor" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Materials $</Label>
                <Input type="number" step="0.01" value={form.material_cost} onChange={(e) => setForm({ ...form, material_cost: e.target.value })} className="h-12 mt-1.5" data-testid="service-material" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Flat rate $</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-12 mt-1.5" data-testid="service-price" />
              </div>
            </div>
            <Button type="submit" disabled={saving} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="service-save">
              {saving ? "Saving…" : editing ? "Save changes" : "Add to price book"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
